import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * ProtectedRoute - Wrapper for routes that require authentication
 * Checks localStorage first (for password login), then falls back to cookie check
 */
const ProtectedRoute = ({ children }) => {
  const location = useLocation();
  
  // Check localStorage synchronously on initial render
  const getInitialAuthState = () => {
    // If user data was passed from AuthCallback
    if (location.state?.user) {
      return { isAuth: true, userData: location.state.user };
    }
    
    // Check localStorage (for password login)
    const storedAuth = localStorage.getItem('isAuthenticated');
    const storedUser = localStorage.getItem('user');
    
    if (storedAuth === 'true' && storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        return { isAuth: true, userData };
      } catch (e) {
        console.error('Failed to parse stored user:', e);
      }
    }
    
    return { isAuth: null, userData: null };
  };
  
  const initialState = getInitialAuthState();
  const [isAuthenticated, setIsAuthenticated] = useState(initialState.isAuth);
  const [user, setUser] = useState(initialState.userData);
  const [checking, setChecking] = useState(initialState.isAuth === null);

  useEffect(() => {
    // Skip if already authenticated via localStorage or location.state
    if (isAuthenticated === true) {
      setChecking(false);
      return;
    }

    // CRITICAL: If returning from OAuth callback, skip the check
    if (window.location.hash?.includes('session_id=')) {
      return;
    }

    const checkAuth = async () => {
      try {
        // Try cookie-based auth
        const response = await fetch(`${API}/api/auth/me`, {
          credentials: 'include'
        });

        if (response.ok) {
          const userData = await response.json();
          setUser(userData);
          setIsAuthenticated(true);
          // Store in localStorage for persistence
          localStorage.setItem('user', JSON.stringify(userData));
          localStorage.setItem('isAuthenticated', 'true');
        } else {
          // Cookie auth failed and no localStorage data
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        setIsAuthenticated(false);
      } finally {
        setChecking(false);
      }
    };

    checkAuth();
  }, [isAuthenticated]);

  // Still checking authentication
  if (checking) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-green-500 mx-auto mb-4" />
          <p className="text-gray-400">Verifying access...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - redirect to login
  if (!isAuthenticated) {
    // Clear any stale localStorage
    localStorage.removeItem('user');
    localStorage.removeItem('isAuthenticated');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Authenticated - render children with user context
  return (
    <UserContext.Provider value={{ user, setUser }}>
      {children}
    </UserContext.Provider>
  );
};

// User context for authenticated pages
export const UserContext = React.createContext(null);

export const useUser = () => {
  const context = React.useContext(UserContext);
  if (!context) {
    return { user: null, setUser: () => {} };
  }
  return context;
};

export default ProtectedRoute;
